/** Public-facing blog data: read published posts from PocketBase, with a
 * graceful fallback to the static data.ts content when PB is unreachable. */
import { pb, fileUrl } from '@/lib/pb';
import { COLLECTIONS, type Post } from '@/lib/types';

export interface PublicPost {
  slug: string;
  title: string;
  summary: string;
  contentHtml: string;
  imageUrl: string;
  categoryName: string;
  date: string;
}

function toPublic(p: Post): PublicPost {
  return {
    slug: p.slug,
    title: p.title,
    summary: p.summary,
    contentHtml: p.content ?? '',
    imageUrl: p.image ? fileUrl(p, p.image) : '',
    categoryName: p.expand?.category?.name ?? '',
    date: p.published_at || p.created,
  };
}

/** Returns published posts, or null if PB is unreachable (→ caller falls back). */
export async function loadPublishedPosts(): Promise<PublicPost[] | null> {
  try {
    const res = await pb.collection(COLLECTIONS.posts).getList<Post>(1, 50, {
      filter: 'status = "published"',
      sort: '-published_at,-created',
      expand: 'category',
    });
    return res.items.map(toPublic);
  } catch {
    return null;
  }
}

/** Returns one published post by slug, null if not found, or throws-free null on error. */
export async function loadPublishedPost(slug: string): Promise<PublicPost | null> {
  try {
    const rec = await pb.collection(COLLECTIONS.posts).getFirstListItem<Post>(
      `slug = ${JSON.stringify(slug)} && status = "published"`,
      { expand: 'category' },
    );
    return toPublic(rec);
  } catch {
    return null;
  }
}
